import React, { useState } from "react";
import { CreditCard, Sparkles, Search, CheckCircle2, Calendar, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) {
    return d;
  }
}

export default function FidelityPage() {
  const [cardNumber, setCardNumber] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardNumber.trim()) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await api.get(`/fidelity/${encodeURIComponent(cardNumber.trim())}`);
      setData(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante la ricerca.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-surface border-b border-line">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-brand-light text-brand-darker text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> Programma fedeltà
          </div>
          <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl text-navy leading-tight">
            La tua <span className="text-brand-dark">Carta Fedeltà</span>
          </h1>
          <p className="mt-4 text-lg text-navy-soft">
            Inserisci il numero della tua carta per vedere il saldo punti e i servizi che hai attivato.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <div className="relative flex-1">
              <CreditCard className="w-5 h-5 text-navy-soft absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                data-testid="fidelity-card-input"
                placeholder="Es. FID-000123"
                className="w-full bg-white border border-line focus:border-brand rounded-full pl-12 pr-4 py-3.5 outline-none transition-colors text-navy"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="fidelity-submit"
              className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-6 py-3.5 rounded-full transition-colors soft-shadow disabled:opacity-60"
            >
              <Search className="w-4 h-4" /> {loading ? "Cerco…" : "Visualizza saldo"}
            </button>
          </form>

          {error && (
            <div className="mt-5 text-sm text-destructive font-medium" data-testid="fidelity-error">{error}</div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-4xl mx-auto w-full px-5 sm:px-8 py-12 flex-1">
        {data ? (
          <div className="animate-fade-up" data-testid="fidelity-result">
            {/* Balance card */}
            <div className="relative overflow-hidden rounded-2xl bg-navy text-white p-8 soft-shadow-lg">
              <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-brand/20" />
              <div className="absolute -right-4 top-16 w-24 h-24 rounded-full bg-brand/20" />
              <div className="relative flex items-start justify-between">
                <div>
                  <div className="label-eyebrow text-brand">Carta Fedeltà</div>
                  <div className="mt-1 font-heading font-bold text-xl">{data.holder_name || "Cliente"}</div>
                  <div className="mt-1 font-mono text-sm text-white/70" data-testid="fidelity-number">{data.card_number}</div>
                </div>
                <CreditCard className="w-8 h-8 text-brand" />
              </div>
              <div className="relative mt-8">
                <div className="text-white/70 text-sm">Saldo punti</div>
                <div className="font-heading font-extrabold text-5xl text-brand" data-testid="fidelity-balance">{data.points}</div>
              </div>
            </div>

            {/* Member-get-member share */}
            {data.referral_code && (
              <div className="mt-6 rounded-2xl border border-brand/30 bg-brand-light/50 p-6" data-testid="fidelity-share">
                <div className="flex items-center gap-2 text-brand-darker font-bold">
                  <Gift className="w-5 h-5" /> Presenta un amico e guadagna punti extra
                </div>
                <p className="mt-2 text-sm text-navy-soft">
                  Condividi il tuo link personale: quando un amico attiva un servizio, ricevi punti extra sulla tua carta.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/?ref=${data.referral_code}`}
                    onFocus={(e) => e.target.select()}
                    data-testid="fidelity-share-url"
                    className="flex-1 bg-white border border-line rounded-full px-4 py-3 text-xs font-mono text-navy outline-none"
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${data.referral_code}`); toast.success("Link copiato!"); }}
                    data-testid="fidelity-share-copy"
                    className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-5 py-3 rounded-full transition-colors shrink-0"
                  >
                    <Copy className="w-4 h-4" /> Copia link
                  </button>
                </div>
              </div>
            )}

            {/* Services / entries */}
            <h2 className="mt-10 font-heading font-bold text-2xl text-navy">Servizi attivati</h2>
            <p className="text-sm text-navy-soft mt-1">I servizi che ti hanno fatto accumulare punti.</p>

            {data.entries && data.entries.length > 0 ? (
              <div className="mt-5 space-y-3" data-testid="fidelity-entries">
                {data.entries.map((e) => (
                  <div key={e.id} className="bg-white rounded-2xl border border-line p-5 flex items-center justify-between gap-4 soft-shadow">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-brand-light text-brand-darker flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-navy truncate">{e.service_name}</div>
                        <div className="flex items-center gap-3 text-xs text-navy-soft mt-0.5">
                          {e.category && <span>{e.category}</span>}
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(e.date)}</span>
                        </div>
                        {e.note && <div className="text-xs text-navy-soft mt-1">{e.note}</div>}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-heading font-extrabold text-xl text-brand-dark">+{e.points}</div>
                      <div className="text-[11px] text-navy-soft uppercase tracking-wide">punti</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-5 bg-surface rounded-2xl border border-line py-12 text-center text-navy-soft text-sm">
                Nessun servizio registrato al momento.
              </div>
            )}
          </div>
        ) : (
          !error && (
            <div className="text-center text-navy-soft py-10">
              <CreditCard className="w-12 h-12 mx-auto text-line mb-4" />
              <p className="text-sm">Inserisci il numero della tua carta qui sopra per visualizzare i tuoi punti.</p>
            </div>
          )
        )}
      </section>

      <Footer />
    </div>
  );
}
